import { Stack, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { AccessibleClinic } from './AccessibleClinic';
import { MapLocation } from './MapLocation';

interface IAddresses {
  addresses: {
    firstAddress: IAddress;
    secondAddress: IAddress;
  };
  isAccessible: boolean;
  coordinates: {
    firstLatitude: number | undefined;
    firstLongitude: number | undefined;
    secondLatitude: number | undefined;
    secondLongitude: number | undefined;
  };
}

export interface IAddress {
  street: string;
  houseNumber: string;
  cityId: number | undefined;
}

export const Address = ({
  addresses,
  isAccessible,
  coordinates,
}: IAddresses) => {
  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Suppliers).resultPage
    .therapy;

  const { firstLatitude, firstLongitude, secondLatitude, secondLongitude } =
    coordinates;

  const cities = useAppSelector(
    ({ generalOptions: { generalOptions } }) => generalOptions
  )?.cities;

  const findCity = (cityId: number | undefined) => {
    if (!cityId) return '';
    const city = cities?.find((city: any) => city.id === cityId)?.value;
    return city ? `, ${city}` : '';
  };

  return (
    <Stack gap='8px'>
      {isDesktop && (
        <Typography
          variant='body1'
          component='h4'
          sx={{
            width: 'fit-content',
            cursor: 'text',
          }}
        >
          {dictionary.address}
        </Typography>
      )}
      {addresses.firstAddress.street || addresses.secondAddress.street ? (
        <>
          {addresses.firstAddress.street && (
            <Stack
              direction={isDesktop ? 'column' : 'row'}
              gap={isDesktop ? '4px' : '8px'}
            >
              <Typography
                variant='body3'
                sx={{
                  width: 'fit-content',
                  cursor: 'text',
                }}
              >
                {addresses.firstAddress.street}{' '}
                {addresses.firstAddress.houseNumber || ''}
                {findCity(addresses.firstAddress.cityId)}
              </Typography>
              {firstLatitude !== undefined && firstLongitude !== undefined && (
                <MapLocation
                  latitude={firstLatitude}
                  longitude={firstLongitude}
                />
              )}
            </Stack>
          )}
          {addresses.secondAddress.street && (
            <Stack
              direction={isDesktop ? 'column' : 'row'}
              gap={isDesktop ? '4px' : '8px'}
            >
              <Typography
                variant='body3'
                sx={{
                  width: 'fit-content',
                  cursor: 'text',
                }}
              >
                {addresses.secondAddress.street}{' '}
                {addresses.secondAddress.houseNumber || ''}
                {findCity(addresses.secondAddress.cityId)}
              </Typography>
              {secondLatitude !== undefined &&
                secondLongitude !== undefined && (
                  <MapLocation
                    latitude={secondLatitude}
                    longitude={secondLongitude}
                  />
                )}
            </Stack>
          )}
        </>
      ) : (
        <Typography
          variant='body3'
          sx={{
            width: 'fit-content',
            cursor: 'text',
          }}
        >
          {isDesktop ? dictionary.noData : dictionary.noAddress}
        </Typography>
      )}
      {isDesktop && isAccessible && <AccessibleClinic />}
    </Stack>
  );
};
